// src/components/CaregiverDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { API_BASE_URL, getApiHeaders } from '../config/api';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from "axios";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";

const API_BASE = API_BASE_URL; // Import from centralized config
const EMOJI = ["😊","🙂","😐","😢","😠"]; // 5 emoji diverse: Felice, Bene, Neutrale, Triste, Arrabbiato

// Sistema valori per calcolo medie (1-5 scale)
const EMOJI_VALUES = {
  '😊': 5, // Felice
  '🙂': 4, // Bene
  '😐': 3, // Neutrale
  '😢': 2, // Triste
  '😠': 1  // Arrabbiato
};

// Calcola media giornaliera e restituisce emoji + label
function calculateDailyAverage(moods) {
  if (!moods || moods.length === 0) {
    return { value: 0, emoji: '😐', label: 'Nessun dato' };
  }

  const sum = moods.reduce((acc, m) => acc + (EMOJI_VALUES[m.emoji] || 3), 0);
  const avg = sum / moods.length;

  if (avg >= 4.5) return { value: avg, emoji: '😊', label: 'Ottima' };
  if (avg >= 3.5) return { value: avg, emoji: '🙂', label: 'Buona' };
  if (avg >= 2.5) return { value: avg, emoji: '😐', label: 'Normale' };
  if (avg >= 1.5) return { value: avg, emoji: '😢', label: 'Difficile' };
  return { value: avg, emoji: '😠', label: 'Critica' };
}

// Calcola trend rispetto a ieri
function getTrend(todayAvg, yesterdayAvg) {
  if (!yesterdayAvg || yesterdayAvg === 0) return { icon: '➡️', label: 'stabile' };
  const diff = todayAvg - yesterdayAvg;
  if (diff > 0.5) return { icon: '📈', label: 'migliora' };
  if (diff < -0.5) return { icon: '📉', label: 'peggiora' };
  return { icon: '➡️', label: 'stabile' };
}

// Formatta data con "Oggi", "Ieri" o data completa
function formatMealDate(dateString) {
  const mealDate = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const isSameDay = (d1, d2) => {
    return d1.getDate() === d2.getDate() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getFullYear() === d2.getFullYear();
  };

  if (isSameDay(mealDate, today)) return 'Oggi';
  if (isSameDay(mealDate, yesterday)) return 'Ieri';

  return mealDate.toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

// Raggruppa pasti per data
function groupMealsByDate(meals) {
  const grouped = {};

  meals.forEach(meal => {
    const date = meal.date || new Date().toISOString().split('T')[0];
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(meal);
  });

  // Ordina le date in ordine decrescente (più recente prima)
  return Object.entries(grouped)
    .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
    .map(([date, meals]) => ({
      date,
      meals: meals.sort((a, b) => {
        // Ordina i pasti del giorno per ora
        return (a.time || '').localeCompare(b.time || '');
      })
    }));
}

// Genera riepilogo giornaliero
function getDailySummary(meals) {
  const types = [...new Set(meals.map(m => m.type))];
  const typeLabels = types.map(t => {
    const emoji = {
      'colazione': '🌅',
      'pranzo': '☀️',
      'cena': '🌙',
      'spuntino': '🍎'
    }[t] || '🍽️';
    return `${emoji} ${t.charAt(0).toUpperCase() + t.slice(1)}`;
  });

  return `${meals.length} ${meals.length === 1 ? 'pasto' : 'pasti'} - ${typeLabels.join(', ')}`;
}

// 🎨 Material Design 3 Colors (senza viola)
const colors = {
  primary: '#1976D2',        // Blu moderno
  secondary: '#00BCD4',      // Turchese
  success: '#4CAF50',        // Verde
  error: '#EF5350',          // Rosso
  warning: '#FF9800',        // Arancione
  background: '#F8F9FA',     // Grigio chiarissimo
  surface: '#FFFFFF',        // Bianco
  surfaceVariant: '#F5F5F5', // Grigio ultra chiaro
  outline: '#E0E0E0',        // Grigio outline
  textPrimary: '#1A1A1A',    // Quasi nero
  textSecondary: '#666666',  // Grigio scuro
  textTertiary: '#999999',   // Grigio medio
};

// 🎨 Stili riutilizzabili
const styles = {
  card: {
    background: colors.surface,
    borderRadius: 16,
    padding: 24,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: `1px solid ${colors.outline}`,
    height: '100%',
  },
  cardHeader: {
    fontSize: 20,
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  button: {
    padding: '12px 24px',
    borderRadius: 12,
    border: 'none',
    fontWeight: 600,
    fontSize: 15,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  buttonPrimary: {
    background: colors.primary,
    color: '#fff',
  },
  buttonSecondary: {
    background: colors.surfaceVariant,
    color: colors.textPrimary,
  },
  buttonSuccess: {
    background: colors.success,
    color: '#fff',
  },
  input: {
    width: '100%',
    padding: 12,
    borderRadius: 8,
    border: `1px solid ${colors.outline}`,
    fontSize: 15,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    outline: 'none',
    transition: 'border-color 0.2s ease',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modalContent: {
    background: colors.surface,
    borderRadius: 16,
    padding: 32,
    maxWidth: 600,
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
};

export default function CaregiverDashboard({ onOpenTraining }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [fileToSend, setFileToSend] = useState(null);
  const [moodsTimeline, setMoodsTimeline] = useState([]); // Timeline emozioni multiple di oggi
  const [range, setRange] = useState(7);
  const [memories, setMemories] = useState([]);
  const [meals, setMeals] = useState([]);

  // Modal states
  const [showMemoryForm, setShowMemoryForm] = useState(false);
  const [editingMemory, setEditingMemory] = useState(null);
  const [showMealForm, setShowMealForm] = useState(false);
  const [expandedDays, setExpandedDays] = useState({}); // Traccia quali giorni sono espansi
  const [selectedCategory, setSelectedCategory] = useState(null); // null = mostra categorie, string = mostra ricordi filtrati
    const [aiCategoryLoading, setAiCategoryLoading] = useState(false); // Loading per suggestion AI
  const [showAiResultModal, setShowAiResultModal] = useState(false); // Modal risultati AI
  const [aiResult, setAiResult] = useState(null); // Dati da mostrare nel modal
  const [searchQuery, setSearchQuery] = useState(''); // Query ricerca ricordi

  // Forms data
  const [newMemory, setNewMemory] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().slice(0, 10),
    photoFiles: [],
    category: 'Generale'
  });
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState({});

  const [newMeal, setNewMeal] = useState({
    type: 'colazione',
    time: new Date().toTimeString().slice(0, 5),
    quantity: '',
    notes: ''
  });

  const helpRequests = [
    { question: "Dov'è Renzo?", count: 12, category: "Orientamento", timestamp: new Date(2025, 0, 20, 10, 30).toISOString() },
    { question: "Quando arriva Caterina?", count: 8, category: "Famiglia", timestamp: new Date(2025, 0, 21, 14, 15).toISOString() },
    { question: "Posso uscire oggi?", count: 6, category: "Attività", timestamp: new Date(2025, 0, 22, 9, 45).toISOString() },
    { question: "Dove sono le chiavi?", count: 4, category: "Oggetti", timestamp: new Date(2025, 0, 22, 16, 20).toISOString() },
    { question: "Cosa si mangia oggi?", count: 3, category: "Alimentazione", timestamp: new Date(2025, 0, 23, 12, 0).toISOString() },
  ];

  // Emoji per categorie
  const CATEGORY_EMOJIS = {
    'Famiglia': '👨‍👩‍👧‍👦',
    'Animali': '🐾',
    'Momenti Speciali': '✨',
    'Viaggi': '✈️',
    'Casa': '🏠',
    'Generale': '📁'
  };

  // Filtra ricordi per ricerca
  const filteredMemories = useMemo(() => {
    if (!searchQuery.trim()) return memories;

    const query = searchQuery.toLowerCase();
    return memories.filter(memory => {
      return (
        memory.title?.toLowerCase().includes(query) ||
        memory.description?.toLowerCase().includes(query) ||
        memory.category?.toLowerCase().includes(query) ||
        memory.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    });
  }, [memories, searchQuery]);

  // Raggruppa ricordi per categoria (usa filteredMemories invece di memories)
  const memoriesByCategory = useMemo(() => {
    const grouped = {};
    filteredMemories.forEach(m => {
      const cat = m.category || 'Generale';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(m);
    });
    return grouped;
  }, [filteredMemories]);

  // Categorie con conteggi
  const categories = useMemo(() => {
    const CATEGORY_ORDER = ['Generale', 'Famiglia', 'Animali', 'Momenti Speciali', 'Viaggi', 'Casa'];

    return Object.keys(memoriesByCategory)
      .map(cat => ({
        name: cat,
        emoji: CATEGORY_EMOJIS[cat] || '📁',
        count: memoriesByCategory[cat].length
      }))
      .sort((a, b) => {
        // Generale sempre primo
        const indexA = CATEGORY_ORDER.indexOf(a.name);
        const indexB = CATEGORY_ORDER.indexOf(b.name);

        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB; // Ordine predefinito
        }
        if (indexA !== -1) return -1; // a viene prima
        if (indexB !== -1) return 1;  // b viene prima
        return a.name.localeCompare(b.name); // Alfabetico per categorie custom
      });
  }, [memoriesByCategory]);

  // Ricordi della categoria selezionata
  const selectedCategoryMemories = useMemo(() => {
    if (!selectedCategory) return [];
    return memoriesByCategory[selectedCategory] || [];
  }, [selectedCategory, memoriesByCategory]);

  async function load() {
    const { data } = await axios.get(`${API_BASE}/files`, { headers: getApiHeaders() });
    setFiles(data.files || []);
  }

  // ❌ DEPRECATO v2.4: Rimosso vecchio sistema mood
  // async function loadMoods() { ... }

  async function loadMoodsTimeline() {
    try {
      // Carica emozioni ultimi 7 giorni (per timeline completa)
      const { data } = await axios.get(`${API_BASE}/api/moods/week`, { headers: getApiHeaders() });
      setMoodsTimeline(data);
      console.log("✅ Timeline emozioni ultimi 7 giorni:", data.length, "emozioni");
    } catch (err) {
      console.error("❌ Errore caricamento timeline:", err);
    }
  }

  async function loadMemories() {
    try {
      const { data } = await axios.get(`${API_BASE}/api/memories`, { headers: getApiHeaders() });
      setMemories(data);
      console.log("✅ Ricordi caricati dal server:", data);
    } catch (err) {
      console.error("❌ Errore caricamento ricordi:", err);
    }
  }

  async function loadMeals() {
    try {
      const { data } = await axios.get(`${API_BASE}/api/meals`, { headers: getApiHeaders() });
      setMeals(data);
      console.log("✅ Pasti caricati dal server:", data);
    } catch (err) {
      console.error("❌ Errore caricamento pasti:", err);
    }
  }

  useEffect(()=>{
    load().catch(console.error);
    loadMoodsTimeline().catch(console.error);
    loadMemories().catch(console.error);
    loadMeals().catch(console.error);
  },[]);

  // Handle navigation from RicordiPage - load memory for editing
  useEffect(() => {
    const editMemoryId = location.state?.editMemoryId;
    const returnTo = location.state?.returnTo;

    // Save the current path or the provided returnTo path
    if (editMemoryId) {
      // If someone explicitly passed returnTo, use that; otherwise use current path
      const pathToReturn = returnTo || location.pathname;
      window.sessionStorage.setItem('returnTo', pathToReturn);
    }

    if (editMemoryId) {
      // Fetch the memory from the API
      axios.get(`${API_BASE}/api/memories/${editMemoryId}`, { headers: getApiHeaders() })
        .then(({ data }) => {
          if (data.success && data.memory) {
            // Set the memory for editing
            setEditingMemory(data.memory);
            // Open the edit form
            setShowMemoryForm(true);
            // Clear the navigation state to prevent reopening on refresh
            window.history.replaceState({}, document.title);
          }
        })
        .catch(err => {
          console.error('Error loading memory for edit:', err);
          alert('Impossibile caricare il ricordo per la modifica.');
        });
    }
  }, [location.state]);

  // Populate form fields when editingMemory changes
  useEffect(() => {
    if (editingMemory) {
      setNewMemory({
        title: editingMemory.title || '',
        description: editingMemory.description || '',
        date: editingMemory.date || '',
        photoFiles: [],
        category: editingMemory.category || 'Generale'
      });
    }
  }, [editingMemory]);

  async function doUpload() {
    if (!fileToSend) return;
    const form = new FormData();
    form.append("file", fileToSend);
    const { data } = await axios.post(`${API_BASE}/upload`, form, { headers: { 'x-api-key': 'DonatApp2025!' } });
    if (data?.ok) { await load(); setFileToSend(null); const el=document.getElementById("upl"); if(el) el.value=""; }
    else alert("Upload fallito.");
  }

  async function doDelete(item) {
    try {
      await axios.delete(`${API_BASE}/delete`, { data: { url:item.url, savedAs:item.name }, headers: getApiHeaders() });
      setFiles(prev => prev.filter(f => f.url !== item.url));
    } catch(e){ console.error(e); alert("Impossibile eliminare il file sul server."); }
  }

  // ❌ DEPRECATO v2.4: Rimosso vecchio sistema addMood
  // Ora si usa solo addMoodToTimeline()

  async function addMoodToTimeline(emoji) {
    try {
      await axios.post(`${API_BASE}/api/moods/add`, {
        emoji: emoji,
        note: "Aggiunto dalla dashboard"
      }, { headers: getApiHeaders() });
      console.log("✅ Emozione aggiunta alla timeline:", emoji);
      await loadMoodsTimeline();
    } catch (err) {
      console.error("❌ Errore aggiunta emozione:", err);
    }
  }

  // ❌ DEPRECATO v2.4: Rimosso vecchio sistema updateMood
  // Il modal di modifica non è più necessario con la timeline

  // 🤖 Funzione SemanticAI per suggestion categoria
  async function getAISuggestion() {
    try {
      setAiCategoryLoading(true);
      const response = await axios.post(`${API_BASE}/api/memories/suggest-category`, {
        title: newMemory.title,
        description: newMemory.description
      }, { headers: getApiHeaders() });

      if (response.data && response.data.category) {
        // 🔥 RAG: Aggiorna titolo, descrizione E categoria con testi arricchiti
        setNewMemory({
          ...newMemory,
          title: response.data.improvedTitle || newMemory.title,
          description: response.data.improvedDescription || newMemory.description,
          category: response.data.category
        });

        // Salva risultati per il modal
        setAiResult(response.data);
        setShowAiResultModal(true);
      }
    } catch (error) {
      console.error('Errore SemanticAI:', error);
      alert('❌ Errore nel suggerimento categoria: ' + (error.response?.data?.error || error.message));
    } finally {
      setAiCategoryLoading(false);
    }
  }

  async function saveMemory() {
    try {
      if (!newMemory.title || !newMemory.date) {
        alert("Titolo e data sono obbligatori");
        return;
      }

      let photos = editingMemory?.photos || null;

      // 📸 Upload foto se presente (usa endpoint memories per consistenza con Android)
      if (newMemory.photoFiles && newMemory.photoFiles.length > 0) {
        const formData = new FormData();
        newMemory.photoFiles.forEach(file => {
          formData.append("photos", file);
        });
        const uploadRes = await axios.post(`${API_BASE}/api/memories/upload-photos`, formData, { headers: { 'x-api-key': 'DonatApp2025!' } });
        photos = uploadRes.data.photos;
        console.log(`📸 ${photos.length} foto caricate:`, photos);
      }

      const memoryData = {
        title: newMemory.title,
        description: newMemory.description,
        date: newMemory.date,
        photos: photos,
        category: newMemory.category || 'Generale'
      };

      if (editingMemory) {
        // Update existing memory
        await axios.put(`${API_BASE}/api/memories/${editingMemory.id}`, memoryData, { headers: getApiHeaders() });
        console.log("✅ Ricordo aggiornato");
        alert("✅ Ricordo aggiornato con successo!");
      } else {
        // Create new memory
        await axios.post(`${API_BASE}/api/memories`, memoryData, { headers: getApiHeaders() });
        console.log("✅ Ricordo salvato");
        alert("✅ Ricordo salvato con successo!");
      }

      setNewMemory({
        title: '',
        description: '',
        date: new Date().toISOString().slice(0, 10),
        photoFiles: [],
        category: 'Generale'
      });
      setShowMemoryForm(false);
      setEditingMemory(null);
      await loadMemories();

      // Navigate back to the origin page
      const returnTo = window.sessionStorage.getItem('returnTo');
      if (returnTo) {
        window.sessionStorage.removeItem('returnTo');
        navigate(returnTo);
      }
    } catch (err) {
      console.error("❌ Errore salvataggio ricordo:", err);
      alert("Errore salvataggio ricordo: " + err.message);
    }
  }

  async function deleteMemory(memoryId) {
    try {
      if (!window.confirm("Sei sicuro di voler eliminare questo ricordo?")) {
        return;
      }

      await axios.delete(`${API_BASE}/api/memories/${memoryId}`, { headers: getApiHeaders() });
      console.log("✅ Ricordo eliminato");
      alert("✅ Ricordo eliminato con successo!");
      await loadMemories();
    } catch (err) {
      console.error("❌ Errore eliminazione ricordo:", err);
      alert("Errore eliminazione ricordo: " + err.message);
    }
  }

  function openEditMemory(memory) {
    setEditingMemory(memory);
    setNewMemory({
      title: memory.title,
      description: memory.description || '',
      date: memory.date,
      photoFiles: [],
      category: memory.category || 'Generale'
    });
    setShowMemoryForm(true);
  }

  async function saveMeal() {
    try {
      if (!newMeal.type || !newMeal.time) {
        alert("Tipo e ora sono obbligatori");
        return;
      }

      await axios.post(`${API_BASE}/api/meals`, {
        type: newMeal.type,
        time: newMeal.time,
        quantity: newMeal.quantity,
        notes: newMeal.notes,
        date: new Date().toISOString().slice(0, 10)
      }, { headers: getApiHeaders() });

      console.log("✅ Pasto salvato");

      setNewMeal({
        type: 'colazione',
        time: new Date().toTimeString().slice(0, 5),
        quantity: '',
        notes: ''
      });
      setShowMealForm(false);
      await loadMeals();
    } catch (err) {
      console.error("❌ Errore salvataggio pasto:", err);
      alert("Errore salvataggio pasto: " + err.message);
    }
  }

  // ❌ DEPRECATO v2.4: Rimossi filtered e byDay (vecchio sistema mood)
  // Ora si usa solo timelineByDay

  // Count today's meals
  const todayMeals = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return meals.filter(m => m.date === today).length;
  }, [meals]);

  // Filtra emozioni di oggi (per timeline blu in alto)
  const todayMoodsOnly = useMemo(() => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return moodsTimeline.filter(m => m.timestamp.startsWith(today));
  }, [moodsTimeline]);

  // Raggruppa emozioni timeline per giorno (ultimi 7 giorni) con medie
  const timelineByDay = useMemo(() => {
    const map = new Map();
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    moodsTimeline.forEach(mood => {
      const moodDate = new Date(mood.timestamp);
      if (moodDate >= sevenDaysAgo) {
        const dateKey = mood.timestamp.split(' ')[0]; // YYYY-MM-DD
        const arr = map.get(dateKey) || [];
        arr.push(mood);
        map.set(dateKey, arr);
      }
    });

    // Ordina per data decrescente (più recenti prima)
    const sortedDays = [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));

    return sortedDays.map(([date, moods], index) => {
      // Calcola emoji predominante (più frequente)
      const emojiCounts = {};
      moods.forEach(m => {
        emojiCounts[m.emoji] = (emojiCounts[m.emoji] || 0) + 1;
      });
      const predominantEmoji = Object.entries(emojiCounts)
        .sort((a, b) => b[1] - a[1])[0]?.[0] || '😐';

      // Calcola media giornaliera
      const dailyAvg = calculateDailyAverage(moods);

      // Calcola trend (confronta con giorno precedente)
      let trend = { icon: '➡️', label: 'stabile' };
      if (index < sortedDays.length - 1) {
        const yesterdayMoods = sortedDays[index + 1][1];
        const yesterdayAvg = calculateDailyAverage(yesterdayMoods);
        trend = getTrend(dailyAvg.value, yesterdayAvg.value);
      }

      // Ultima emozione del giorno
      const sortedMoods = moods.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
      const lastMood = sortedMoods[0];

      return {
        date,
        moods: sortedMoods,
        predominantEmoji,
        count: moods.length,
        dailyAvg,
        trend,
        lastMood
      };
    });
  }, [moodsTimeline]);

  // 📊 CHART DATA - Usa medie calcolate dalla timeline (non conteggi!)
  const chartData = useMemo(() => {
    if (!timelineByDay || timelineByDay.length === 0) return [];

    // Usa timelineByDay che ha già le medie calcolate
    return timelineByDay.map(({ date, dailyAvg }) => ({
      giorno: new Date(date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }), // "18 nov"
      valore: dailyAvg.value, // Media da 1-5 (es: 2.4)
      emoji: dailyAvg.emoji,  // Emoji media (es: 😢)
      label: dailyAvg.label   // Label (es: "Difficile")
    }));
  }, [timelineByDay]);

  const toggleDayExpansion = (date) => {
    setExpandedDays(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  return (
    <div style={{
      maxWidth: 1400,
      margin: '0 auto',
      padding: 24,
      background: colors.background,
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>

      <div className="page-header green">
        <h1>Dashboard Caregiver</h1>
        <p className="subtitle">Monitora e supporta il benessere quotidiano</p>
      </div>

      {/* 🔍 BARRA RICERCA */}
      <div style={{
        marginBottom: '24px',
      }}>
        <input
          type="text"
          placeholder="🔍 Cerca ricordi, persone, momenti..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '16px 20px',
            fontSize: '16px',
            border: '1px solid #e0e0e0',
            borderRadius: '12px',
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            outline: 'none',
            transition: 'all 0.2s ease',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#10b981';
            e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.15)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e0e0e0';
            e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
          }}
        />
      </div>

      {/* 📊 STATISTICHE RAPIDE */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px',
      }}>
        {/* Totale Ricordi */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '16px',
          border: '1px solid #f0f0f0',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
            📚 Totale Ricordi
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#1a1a1a' }}>
            {memories.length}
          </div>
        </div>

        {/* Questa Settimana */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '16px',
          border: '1px solid #f0f0f0',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
            ✨ Questa Settimana
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#10b981' }}>
            {memories.filter(m => {
              const date = new Date(m.date);
              const weekAgo = new Date();
              weekAgo.setDate(weekAgo.getDate() - 7);
              return date >= weekAgo;
            }).length}
          </div>
        </div>

        {/* Categorie Attive */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '16px',
          border: '1px solid #f0f0f0',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
            📁 Categorie Attive
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#6366f1' }}>
            {[...new Set(memories.map(m => m.category))].length}
          </div>
        </div>

        {/* Con SemanticAI */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '16px',
          border: '1px solid #f0f0f0',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
            ❤️ Con SemanticAI
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#ec4899' }}>
            {memories.filter(m => m.description && m.description.length > 100).length}
          </div>
        </div>
      </div>

      {/* 📊 LAYOUT 2x2 GRID - Responsive */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 500px), 1fr))',
        gap: 24,
        marginBottom: 32,
      }}>

        {/* 📂 CARD CATEGORIE RICORDI */}
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{ ...styles.cardHeader, marginBottom: 0 }}>
              <span>{selectedCategory ? CATEGORY_EMOJIS[selectedCategory] : '📂'}</span>
              <span>{selectedCategory || 'Categorie Ricordi'}</span>
              {!selectedCategory && (
                <span style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: colors.textSecondary,
                  marginLeft: 8
                }}>
                  ({filteredMemories.length}{searchQuery && ` di ${memories.length}`})
                </span>
              )}
            </h2>
            <div style={{ display: 'flex', gap: 8 }}>
              {selectedCategory && (
                <button
                  onClick={() => setSelectedCategory(null)}
                  style={{
                    ...styles.button,
                    ...styles.buttonSecondary,
                    padding: '8px 16px',
                    fontSize: 14,
                  }}
                >
                  ← Indietro
                </button>
              )}
              <button
                onClick={()=>{
                  setEditingMemory(null);
                  setNewMemory({
                    title: '',
                    description: '',
                    date: new Date().toISOString().slice(0, 10),
                    photoFiles: [],
                    category: 'Generale'
                  });
                  setShowMemoryForm(true);
                }}
                style={{
                  ...styles.button,
                  ...styles.buttonSuccess,
                  padding: '8px 16px',
                  fontSize: 14,
                }}
              >
                + Nuovo
              </button>
            </div>
          </div>

          <div style={{
            maxHeight: 400,
            overflow: 'auto',
            paddingRight: 8,
          }}>
            {filteredMemories.length === 0 ? (
              <div style={{
                color: colors.textSecondary,
                padding: 40,
                textAlign: 'center',
                background: colors.surfaceVariant,
                borderRadius: 12,
              }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>
                  {searchQuery ? '🔍' : '📝'}
                </div>
                <div style={{ fontSize: 14 }}>
                  {searchQuery ? `Nessun ricordo trovato per "${searchQuery}"` : 'Nessun ricordo salvato'}
                </div>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    style={{
                      marginTop: 16,
                      padding: '8px 16px',
                      backgroundColor: colors.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    Cancella ricerca
                  </button>
                )}
              </div>
            ) : !selectedCategory ? (
              // VISTA CATEGORIE (come su Android)
              <div style={{ display: 'grid', gap: 12 }}>
                {categories.map(category => (
                  <div
                    key={category.name}
                    onClick={() => setSelectedCategory(category.name)}
                    style={{
                      background: 'white',
                      borderRadius: 16,
                      padding: 20,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      border: '1px solid #f0f0f0',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      display: 'flex',
                      gap: 16,
                      alignItems: 'center',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.12)';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                      e.currentTarget.style.borderColor = '#f0f0f0';
                    }}
                  >
                    {/* Emoji categoria */}
                    <div style={{
                      fontSize: 40,
                      lineHeight: 1,
                    }}>
                      {category.emoji}
                    </div>

                    {/* Info categoria */}
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: 600,
                        fontSize: 18,
                        color: '#1a1a1a',
                        letterSpacing: '-0.01em',
                      }}>
                        {category.name}
                      </div>
                      <div style={{
                        fontSize: 14,
                        color: '#666',
                        marginTop: 4,
                      }}>
                        {category.count} {category.count === 1 ? 'ricordo' : 'ricordi'}
                      </div>
                    </div>

                    {/* Arrow iOS */}
                    <div style={{
                      fontSize: 32,
                      color: '#C7C7CC',
                      lineHeight: 1,
                    }}>
                      ›
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // VISTA RICORDI FILTRATI
              <div style={{ display: 'grid', gap: 12 }}>
                {selectedCategoryMemories.map(memory => (
                  <div
                    key={memory.id}
                    onClick={() => openEditMemory(memory)}
                    style={{
                      background: 'white',
                      borderRadius: 16,
                      padding: 16,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      border: '1px solid #f0f0f0',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      display: 'flex',
                      gap: 12,
                      alignItems: 'center',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.12)';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                      e.currentTarget.style.borderColor = '#f0f0f0';
                    }}
                  >
                    {/* Miniatura 60x60px */}
                    {(memory.photos && Array.isArray(memory.photos) && memory.photos.length > 0) ? (
                      <img
                        src={`${API_BASE}${memory.photos[currentPhotoIndex[memory.id] || 0]}`}
                        alt={memory.title}
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 8,
                          flexShrink: 0,
                          background: '#fff',
                        }}
                      />
                    ) : (
                      <div style={{
                        width: 60,
                        height: 60,
                        borderRadius: 8,
                        background: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 24,
                        flexShrink: 0,
                      }}>
                        📝
                      </div>
                    )}

                    {/* Titolo solo (stile snello iOS/Google) */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 600,
                        color: '#1a1a1a',
                        fontSize: 18,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        letterSpacing: '-0.01em',
                      }}>
                        {memory.title}
                      </div>
                      {memory.date && (
                        <div style={{
                          fontSize: 12,
                          color: '#999',
                          marginTop: 2,
                        }}>
                          {new Date(memory.date).toLocaleDateString('it-IT', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                      )}
                    </div>

                    {/* Arrow iOS style */}
                    <div style={{
                      fontSize: 28,
                      color: '#C7C7CC',
                      lineHeight: 1,
                    }}>
                      ›
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 🍽️ CARD PASTI */}
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{ ...styles.cardHeader, marginBottom: 0 }}>
              <span>🍽️</span>
              <span>Pasti</span>
            </h2>
            <button
              onClick={()=>setShowMealForm(!showMealForm)}
              style={{
                ...styles.button,
                ...styles.buttonSuccess,
                padding: '8px 16px',
                fontSize: 14,
              }}
            >
              {showMealForm ? '✕ Chiudi' : '+ Nuovo'}
            </button>
          </div>

          {/* Counter oggi */}
          <div style={{
            background: `linear-gradient(135deg, ${colors.warning} 0%, ${colors.error} 100%)`,
            borderRadius: 12,
            padding: 16,
            marginBottom: 20,
            color: '#fff',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 36, fontWeight: 700 }}>{todayMeals}</div>
            <div style={{ fontSize: 14, opacity: 0.9 }}>registrati oggi</div>
          </div>

          {/* Form */}
          {showMealForm && (
            <div style={{
              background: colors.surfaceVariant,
              borderRadius: 12,
              padding: 16,
              marginBottom: 20,
            }}>
              <div style={{ display: 'grid', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13, color: colors.textPrimary }}>
                    Tipo*
                  </label>
                  <select
                    value={newMeal.type}
                    onChange={(e)=>setNewMeal({...newMeal, type: e.target.value})}
                    style={{...styles.input, padding: 10, cursor: 'pointer'}}
                  >
                    <option value="colazione">🌅 Colazione</option>
                    <option value="pranzo">☀️ Pranzo</option>
                    <option value="cena">🌙 Cena</option>
                    <option value="spuntino">🍎 Spuntino</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13, color: colors.textPrimary }}>
                    Ora*
                  </label>
                  <input
                    type="time"
                    value={newMeal.time}
                    onChange={(e)=>setNewMeal({...newMeal, time: e.target.value})}
                    style={{...styles.input, padding: 10}}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13, color: colors.textPrimary }}>
                    Quantità
                  </label>
                  <input
                    type="text"
                    value={newMeal.quantity}
                    onChange={(e)=>setNewMeal({...newMeal, quantity: e.target.value})}
                    placeholder="Es: mezza porzione"
                    style={{...styles.input, padding: 10}}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13, color: colors.textPrimary }}>
                    Note
                  </label>
                  <textarea
                    value={newMeal.notes}
                    onChange={(e)=>setNewMeal({...newMeal, notes: e.target.value})}
                    placeholder="Es: Pasta al pomodoro"
                    rows={2}
                    style={{...styles.input, padding: 10}}
                  />
                </div>

                <button
                  onClick={saveMeal}
                  style={{
                    ...styles.button,
                    ...styles.buttonPrimary,
                    padding: '10px',
                  }}
                >
                  Salva Pasto
                </button>
              </div>
            </div>
          )}

          {/* Lista pasti raggruppati per giorno */}
          <div style={{ maxHeight: 350, overflow: 'auto' }}>
            {meals.length === 0 ? (
              <div style={{
                color: colors.textSecondary,
                textAlign: 'center',
                padding: 20,
                background: colors.surfaceVariant,
                borderRadius: 8,
                fontSize: 13,
              }}>
                Nessun pasto registrato
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 16 }}>
                {groupMealsByDate(meals).map((group, groupIdx) => (
                  <div key={groupIdx} style={{ marginBottom: 8 }}>
                    {/* Header giorno con riepilogo */}
                    <div>
                      <div style={{
                        height: 2,
                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                        borderRadius: 2,
                        marginBottom: 8,
                      }} />
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: 13,
                        fontWeight: 600,
                        color: colors.textPrimary,
                        marginBottom: 8,
                        paddingLeft: 4,
                      }}>
                        <span>{formatMealDate(group.date)}</span>
                        <span style={{ fontSize: 11, color: colors.textSecondary }}>
                          {getDailySummary(group.meals)}
                        </span>
                      </div>
                    </div>

                    {/* Pasti del giorno */}
                    <div style={{ display: 'grid', gap: 6, paddingLeft: 8 }}>
                      {group.meals.map((meal, mealIdx) => (
                        <div
                          key={mealIdx}
                          style={{
                            background: colors.surfaceVariant,
                            borderRadius: 8,
                            padding: 10,
                            fontSize: 13,
                            borderLeft: `3px solid ${colors.primary}`,
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontWeight: 600, color: colors.textPrimary }}>
                              {meal.type === 'colazione' && '🌅'}
                              {meal.type === 'pranzo' && '☀️'}
                              {meal.type === 'cena' && '🌙'}
                              {meal.type === 'spuntino' && '🍎'}
                              {' '}
                              {meal.type.charAt(0).toUpperCase() + meal.type.slice(1)}
                            </span>
                            <span style={{ color: colors.textSecondary }}>
                              {meal.time}
                            </span>
                          </div>
                          {meal.notes && (
                            <div style={{ color: colors.textSecondary, fontSize: 12 }}>
                              {meal.notes}
                            </div>
                          )}
                          {meal.quantity && (
                            <div style={{ color: colors.textTertiary, fontSize: 11, marginTop: 4 }}>
                              Quantità: {meal.quantity}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 😊 UMORE TIMELINE - Emozioni multiple di oggi */}
        <div style={styles.card}>
          <h2 style={styles.cardHeader}>
            <span>{todayMoodsOnly.length > 0 ? todayMoodsOnly[0].emoji : '😊'}</span>
            <span>Emozioni Oggi</span>
          </h2>

          {/* Timeline emozioni di oggi con orari */}
          <div style={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            borderRadius: 12,
            padding: 20,
            color: '#fff',
            marginBottom: 20,
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, opacity: 0.9 }}>
              Timeline di oggi
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              {todayMoodsOnly.length === 0 ? (
                <div style={{ fontSize: 13, opacity: 0.8 }}>
                  Nessuna emozione registrata oggi
                </div>
              ) : (
                todayMoodsOnly.map((mood) => {
                  const time = new Date(mood.timestamp).toLocaleTimeString('it-IT', {
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                  return (
                    <div
                      key={mood.id}
                      style={{
                        background: 'rgba(255,255,255,0.15)',
                        borderRadius: 8,
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                      title={mood.note || 'Nessuna nota'}
                    >
                      <span style={{ fontSize: 11, opacity: 0.9 }}>{time}</span>
                      <span style={{ fontSize: 20 }}>{mood.emoji}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Aggiungi emozione attuale con bottone "+" */}
          <div style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginBottom: 20,
            paddingBottom: 20,
            borderBottom: `2px solid ${colors.outline}`,
          }}>
            {EMOJI.slice(0, 5).map((em, i) => (
              <button
                key={i}
                onClick={() => addMoodToTimeline(em)}
                style={{
                  fontSize: 32,
                  background: colors.surfaceVariant,
                  border: 'none',
                  borderRadius: 12,
                  padding: 12,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.15)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                title="Aggiungi emozione ora"
              >
                {em}
                <div style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  background: colors.success,
                  color: '#fff',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 700,
                }}>
                  +
                </div>
              </button>
            ))}
          </div>

          {/* Storico ultimi 7 giorni - Card espandibili */}
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary, marginBottom: 12 }}>
              Ultimi 7 giorni
            </div>
            {timelineByDay.length === 0 ? (
              <div style={{
                color: colors.textSecondary,
                textAlign: 'center',
                padding: 20,
                background: colors.surfaceVariant,
                borderRadius: 8,
                fontSize: 13,
              }}>
                Nessuna emozione registrata negli ultimi 7 giorni
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {timelineByDay.map(({ date, moods, predominantEmoji, count, dailyAvg, trend, lastMood }) => {
                  // 🔧 USA DATA LOCALE (non UTC) per confronto "oggi"
                  const now = new Date();
                  const todayLocal = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                  const isToday = date === todayLocal;
                  const isExpanded = expandedDays[date];

                  // Formatta ultima emozione
                  const lastTime = lastMood ? new Date(lastMood.timestamp).toLocaleTimeString('it-IT', {
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : '';

                  return (
                    <div
                      key={date}
                      style={{
                        background: isToday
                          ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`
                          : colors.surface,
                        border: isToday ? 'none' : `1px solid ${colors.outline}`,
                        borderRadius: 12,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        boxShadow: isExpanded ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)',
                      }}
                    >
                      {/* Header cliccabile */}
                      <div
                        onClick={() => toggleDayExpansion(date)}
                        style={{
                          padding: 16,
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'background 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (!isToday) e.currentTarget.style.background = colors.surfaceVariant;
                        }}
                        onMouseLeave={(e) => {
                          if (!isToday) e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                          <div style={{
                            fontSize: 32,
                            lineHeight: 1,
                          }}>
                            {predominantEmoji}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: 600,
                              fontSize: 14,
                              color: isToday ? '#fff' : colors.textPrimary,
                            }}>
                              {isToday ? 'Oggi' : new Date(date).toLocaleDateString('it-IT', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long'
                              })}
                            </div>
                            <div style={{
                              fontSize: 11,
                              color: isToday ? 'rgba(255,255,255,0.8)' : colors.textSecondary,
                              marginTop: 4,
                              display: 'flex',
                              gap: 12,
                              alignItems: 'center',
                              flexWrap: 'wrap',
                            }}>
                              <span>Media: {dailyAvg.value.toFixed(1)} {dailyAvg.emoji} {dailyAvg.label}</span>
                              <span>{trend.icon} {trend.label}</span>
                              {lastMood && <span>Ultima: {lastMood.emoji} ({lastTime})</span>}
                              <span>Tot: {count}</span>
                            </div>
                          </div>
                        </div>
                        <div style={{
                          fontSize: 20,
                          color: isToday ? '#fff' : colors.textSecondary,
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                        }}>
                          ▼
                        </div>
                      </div>

                      {/* Timeline dettagliata (espandibile) */}
                      <div style={{
                        maxHeight: isExpanded ? '500px' : '0',
                        overflow: 'hidden',
                        transition: 'max-height 0.3s ease',
                      }}>
                        <div style={{
                          padding: '0 16px 16px 16px',
                          background: isToday ? 'rgba(255,255,255,0.1)' : colors.surfaceVariant,
                        }}>
                          {moods.map((mood, idx) => {
                            const time = new Date(mood.timestamp).toLocaleTimeString('it-IT', {
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                            return (
                              <div
                                key={mood.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 12,
                                  padding: '8px 12px',
                                  background: isToday ? 'rgba(255,255,255,0.1)' : colors.surface,
                                  borderRadius: 8,
                                  marginTop: idx === 0 ? 8 : 4,
                                }}
                              >
                                <div style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: isToday ? 'rgba(255,255,255,0.9)' : colors.textSecondary,
                                  minWidth: 45,
                                }}>
                                  {time}
                                </div>
                                <div style={{ fontSize: 24, lineHeight: 1 }}>
                                  {mood.emoji}
                                </div>
                                {mood.note && (
                                  <div style={{
                                    fontSize: 12,
                                    color: isToday ? 'rgba(255,255,255,0.8)' : colors.textSecondary,
                                    flex: 1,
                                  }}>
                                    {mood.note}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* 📈 GRAFICO */}
        <div style={styles.card}>
          <h2 style={styles.cardHeader}>
            <span>📈</span>
            <span>Andamento umore</span>
          </h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={colors.primary} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={colors.primary} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="giorno"
                  stroke={colors.textTertiary}
                  style={{ fontSize: 12 }}
                />
                <YAxis
                  stroke={colors.textTertiary}
                  style={{ fontSize: 12 }}
                  domain={[0, 5]}
                  ticks={[0, 1, 2, 3, 4, 5]}
                  tickFormatter={(v) => v.toFixed(1)}
                />
                <Tooltip
                  contentStyle={{
                    background: colors.surface,
                    border: `1px solid ${colors.outline}`,
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value, name, props) => {
                    const { emoji, label } = props.payload;
                    return [`Media: ${value.toFixed(1)} ${emoji} ${label}`, 'Umore'];
                  }}
                  labelFormatter={(date) => `📅 ${date}`}
                />
                <Area type="monotone" dataKey="valore" stroke={colors.primary} strokeWidth={2} fillOpacity={1} fill="url(#colorUv)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{
              color: colors.textSecondary,
              textAlign: 'center',
              padding: 60,
              background: colors.surfaceVariant,
              borderRadius: 12,
            }}>
              Nessun dato disponibile
            </div>
          )}
        </div>

      </div>

      {/* 🎓 Chat AI Training - Solo Caregiver */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        border: '1px solid #f0f0f0',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#202124',
          marginBottom: '12px'
        }}>
          🎓 Addestra Assistente AI
        </h3>

        <p style={{
          fontSize: '14px',
          color: '#5f6368',
          marginBottom: '20px',
          lineHeight: '1.6'
        }}>
          Chatta con l'assistente vocale di Donata per addestrarlo e migliorare le risposte
        </p>

        <button
          onClick={onOpenTraining}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: '#1a73e8',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 8px rgba(26, 115, 232, 0.3)'
          }}
        >
          Apri Chat Training
        </button>
      </div>

      {/* 📊 Report Domande Dettagliato */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid #f0f0f0',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#202124',
          marginBottom: '8px'
        }}>
          📊 Report Domande Assistente
        </h3>

        <div style={{
          fontSize: '14px',
          color: '#5f6368',
          marginBottom: '20px'
        }}>
          Domande frequenti di Donata all'assistente AI
        </div>

        {/* Totale domande */}
        <div style={{
          backgroundColor: '#f0f7ff',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '16px',
          border: '1px solid #1a73e8'
        }}>
          <div style={{ fontSize: '13px', color: '#5f6368', marginBottom: '4px' }}>
            Totale domande registrate
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#1a73e8' }}>
            {helpRequests.length}
          </div>
        </div>

        {/* Domande per categoria */}
        <div style={{
          display: 'grid',
          gap: '12px'
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#202124',
            marginBottom: '8px'
          }}>
            Per categoria:
          </div>

          {(() => {
            const categoryCounts = {};
            helpRequests.forEach(req => {
              const cat = req.category || 'Non categorizzato';
              categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
            });

            return Object.entries(categoryCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([category, count]) => (
                <div
                  key={category}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}
                >
                  <div style={{
                    fontSize: '15px',
                    color: '#202124',
                    fontWeight: '500'
                  }}>
                    {category}
                  </div>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#1a73e8'
                  }}>
                    {count}
                  </div>
                </div>
              ));
          })()}
        </div>

        {/* Top 5 domande recenti */}
        {helpRequests.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#202124',
              marginBottom: '12px'
            }}>
              Ultime 5 domande:
            </div>
            {helpRequests.slice(0, 5).map((req, idx) => (
              <div
                key={idx}
                style={{
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  fontSize: '13px',
                  color: '#5f6368',
                  borderLeft: '3px solid #1a73e8'
                }}
              >
                <div style={{ fontWeight: '600', color: '#202124', marginBottom: '4px' }}>
                  {req.category || 'Generale'}
                </div>
                "{req.question}"
                <div style={{ fontSize: '11px', color: '#9e9e9e', marginTop: '4px' }}>
                  {new Date(req.timestamp).toLocaleString('it-IT')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 🔧 MODAL NUOVO RICORDO CON RAG/SEMANTICAI */}
      {showMemoryForm && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 9998
            }}
            onClick={() => {
              const returnTo = window.sessionStorage.getItem('returnTo');
              setShowMemoryForm(false);
              setEditingMemory(null);
              if (returnTo) {
                window.sessionStorage.removeItem('returnTo');
                navigate(returnTo);
              }
            }}
          />

          {/* Modal */}
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto',
            backgroundColor: 'white',
            borderRadius: '20px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            zIndex: 9999
          }}>

            {/* Header */}
            <div style={{
              padding: '24px 28px',
              borderBottom: '1px solid #e8eaed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '500',
                color: '#202124'
              }}>
                {editingMemory ? 'Modifica ricordo' : 'Nuovo ricordo'}
              </h2>

              <button
                onClick={() => {
                  const returnTo = window.sessionStorage.getItem('returnTo');
                  setShowMemoryForm(false);
                  setEditingMemory(null);
                  if (returnTo) {
                    window.sessionStorage.removeItem('returnTo');
                    navigate(returnTo);
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  color: '#5f6368',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  lineHeight: '1'
                }}
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '24px 28px' }}>

              {/* PULSANTE SEMANTICAI IN ALTO - SUPER VISIBILE */}
              <div style={{
                backgroundColor: '#f0fdf4',
                border: '2px solid #10b981',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '14px',
                  color: '#065f46',
                  marginBottom: '12px',
                  fontWeight: '500',
                  letterSpacing: '0.5px'
                }}>
                  ASSISTENTE AI - GESTIONE MEMORIA RAG
                </div>

                <button
                  onClick={getAISuggestion}
                  disabled={aiCategoryLoading || !newMemory.title || !newMemory.description}
                  style={{
                    width: '100%',
                    padding: '18px',
                    backgroundColor: aiCategoryLoading ? '#e0e0e0' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: aiCategoryLoading || !newMemory.title || !newMemory.description ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    letterSpacing: '0.3px'
                  }}
                >
                  {aiCategoryLoading ? '⏳ Analisi SemanticAI in corso...' : '🚀 Migliora con SemanticAI'}
                </button>

                <div style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginTop: '8px',
                  lineHeight: '1.4'
                }}>
                  Arricchisce titolo, descrizione e suggerisce categoria usando memoria esistente
                </div>
              </div>

              {/* BOX CATEGORIA ATTUALE */}
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '2px solid #dadce0',
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '24px'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#5f6368',
                  marginBottom: '8px',
                  fontWeight: '500',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>
                  Categoria Attuale
                </div>
                <div style={{
                  fontSize: '18px',
                  color: '#202124',
                  fontWeight: '600'
                }}>
                  {newMemory.category || 'Generale'}
                </div>
              </div>

              {/* BOX SUGGERIMENTO SEMANTICAI (appare dopo analisi) */}
              {aiResult && (
                <div style={{
                  backgroundColor: '#e8f5e9',
                  border: '2px solid #4caf50',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px',
                  animation: 'slideDown 0.3s ease'
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: '#2e7d32',
                    marginBottom: '12px',
                    fontWeight: '600',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                  }}>
                    ✨ SUGGERIMENTO SEMANTICAI
                  </div>

                  <div style={{
                    backgroundColor: 'white',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                      Categoria suggerita:
                    </div>
                    <div style={{
                      fontSize: '20px',
                      color: '#1a73e8',
                      fontWeight: '700'
                    }}>
                      {aiResult.category}
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    fontSize: '13px',
                    color: '#5f6368'
                  }}>
                    <div>
                      <span style={{ fontWeight: '600' }}>Score:</span> {aiResult.semanticScore}/100
                    </div>
                    <div>
                      <span style={{ fontWeight: '600' }}>Ricordi usati:</span> {aiResult.usedMemories || 0}
                    </div>
                  </div>

                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#fff3e0',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#e65100',
                    fontWeight: '500'
                  }}>
                    💡 Titolo e descrizione sono stati migliorati dall'AI
                  </div>
                </div>
              )}

              {/* Titolo */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#5f6368',
                  marginBottom: '8px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>
                  Titolo ricordo
                </label>
                <input
                  type="text"
                  value={newMemory.title}
                  onChange={(e) => setNewMemory({...newMemory, title: e.target.value})}
                  placeholder="Inserisci il titolo"
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    fontSize: '16px',
                    color: '#202124',
                    border: '2px solid #dadce0',
                    borderRadius: '8px',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'white'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1a73e8';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#dadce0';
                  }}
                />
              </div>

              {/* Descrizione */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#5f6368',
                  marginBottom: '8px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>
                  Descrizione dettagliata
                </label>
                <textarea
                  value={newMemory.description}
                  onChange={(e) => setNewMemory({...newMemory, description: e.target.value})}
                  placeholder="Descrivi il ricordo"
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    fontSize: '15px',
                    color: '#202124',
                    border: '2px solid #dadce0',
                    borderRadius: '8px',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: '140px',
                    maxHeight: '400px',
                    lineHeight: '1.6',
                    backgroundColor: 'white',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1a73e8';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#dadce0';
                  }}
                />
              </div>

              {/* Data */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#5f6368',
                  marginBottom: '8px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>
                  Data evento
                </label>
                <input
                  type="date"
                  value={newMemory.date}
                  onChange={(e) => setNewMemory({...newMemory, date: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    fontSize: '16px',
                    color: '#202124',
                    border: '2px solid #dadce0',
                    borderRadius: '8px',
                    outline: 'none',
                    backgroundColor: 'white',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1a73e8';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#dadce0';
                  }}
                />
              </div>

              {/* Categoria - DROPDOWN */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#5f6368',
                  marginBottom: '8px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>
                  Categoria finale (modifica se necessario)
                </label>
                <select
                  value={newMemory.category}
                  onChange={(e) => setNewMemory({...newMemory, category: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    fontSize: '16px',
                    color: '#202124',
                    border: '2px solid #dadce0',
                    borderRadius: '8px',
                    outline: 'none',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1a73e8';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#dadce0';
                  }}
                >
                  <option value="Generale">Generale</option>
                  <option value="Famiglia">Famiglia</option>
                  <option value="Animali">Animali</option>
                  <option value="Viaggi">Viaggi</option>
                  <option value="Casa">Casa</option>
                  <option value="Momenti Speciali">Momenti Speciali</option>
                  <option value="Casa_Animali">Casa Animali</option>
                  <option value="Famiglia_Viaggi">Famiglia Viaggi</option>
                  <option value="Famiglia_Animali">Famiglia Animali</option>
                </select>
              </div>

              {/* Foto - Stile Apple Minimalista */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#5f6368',
                  marginBottom: '12px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>
                  Foto (opzionale)
                </label>

                {/* Area upload */}
                <div
                  style={{
                    border: '2px dashed #dadce0',
                    borderRadius: '12px',
                    padding: '40px 24px',
                    textAlign: 'center',
                    backgroundColor: '#fafafa',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#1a73e8';
                    e.currentTarget.style.backgroundColor = '#f0f7ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#dadce0';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }}
                  onClick={() => document.getElementById('fileInput').click()}
                >
                  <div style={{
                    width: '60px',
                    height: '60px',
                    margin: '0 auto 16px',
                    border: '3px solid #dadce0',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{
                      fontSize: '32px',
                      fontWeight: '300',
                      color: '#5f6368'
                    }}>
                      +
                    </div>
                  </div>

                  <div style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#1a73e8',
                    marginBottom: '6px'
                  }}>
                    Carica foto (max 3)
                  </div>

                  <div style={{
                    fontSize: '13px',
                    color: '#5f6368'
                  }}>
                    o trascina qui il file
                  </div>
                </div>

                {/* Input nascosto */}
                <input
                  id="fileInput"
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    const currentFiles = newMemory.photoFiles || [];
                    const totalFiles = [...currentFiles, ...files].slice(0, 3);
                    setNewMemory({...newMemory, photoFiles: totalFiles});
                  }}
                />

                {/* Preview foto caricate (grid) */}
                {newMemory.photoFiles && newMemory.photoFiles.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#2e7d32',
                      marginBottom: '8px'
                    }}>
                      {newMemory.photoFiles.length} {newMemory.photoFiles.length === 1 ? 'foto caricata' : 'foto caricate'}
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                      gap: '12px'
                    }}>
                      {newMemory.photoFiles.map((file, index) => (
                        <div key={index} style={{
                          position: 'relative',
                          width: '100%',
                          paddingBottom: '100%',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '2px solid #4caf50',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                        }}>
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Preview ${index + 1}`}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const newFiles = newMemory.photoFiles.filter((_, i) => i !== index);
                              setNewMemory({...newMemory, photoFiles: newFiles});
                            }}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              border: 'none',
                              backgroundColor: 'rgba(220, 38, 38, 0.9)',
                              color: 'white',
                              fontSize: '16px',
                              fontWeight: '700',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                              lineHeight: '1'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = 'rgba(185, 28, 28, 0.9)';
                              e.target.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'rgba(220, 38, 38, 0.9)';
                              e.target.style.transform = 'scale(1)';
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Foto esistenti (quando si modifica un ricordo) */}
                {editingMemory && (editingMemory.photoUrl || editingMemory.photos) && (!newMemory.photoFiles || newMemory.photoFiles.length === 0) && (() => {
                  // Parse safe delle foto esistenti
                  let photos = [];
                  if (editingMemory.photos) {
                    try {
                      photos = JSON.parse(editingMemory.photos);
                    } catch (e) {
                      // Se non è JSON valido, potrebbe essere una stringa semplice
                      photos = [editingMemory.photos];
                    }
                  } else if (editingMemory.photoUrl) {
                    photos = [editingMemory.photoUrl];
                  }

                  // Ensure photos is always an array
                  if (!Array.isArray(photos)) {
                    photos = photos ? [photos] : [];
                  }

                  if (photos.length === 0) return null;

                  return (
                    <div style={{ marginTop: '16px' }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1a73e8',
                        marginBottom: '8px'
                      }}>
                        {photos.length} {photos.length === 1 ? 'foto esistente' : 'foto esistenti'}
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                        gap: '12px'
                      }}>
                        {photos.map((photoUrl, index) => (
                          <div key={index} style={{
                            position: 'relative',
                            width: '100%',
                            paddingBottom: '100%',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: '2px solid #1a73e8',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                          }}>
                            <img
                              src={`${API_BASE}${photoUrl}`}
                              alt={`Foto ${index + 1}`}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          </div>
                        ))}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#5f6368',
                        marginTop: '8px',
                        fontStyle: 'italic'
                      }}>
                        Carica nuove foto per sostituire quelle esistenti
                      </div>
                    </div>
                  );
                })()}
              </div>

            </div>

            {/* Footer */}
            <div style={{
              padding: '20px 28px 24px',
              borderTop: '1px solid #e8eaed',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              backgroundColor: '#f8f9fa'
            }}>
              <button
                onClick={() => {
                  setShowMemoryForm(false);
                  setEditingMemory(null);
                }}
                style={{
                  padding: '12px 28px',
                  fontSize: '15px',
                  fontWeight: '500',
                  color: '#5f6368',
                  backgroundColor: 'white',
                  border: '2px solid #dadce0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Annulla
              </button>

              {editingMemory && (
                <button
                  onClick={() => {
                    deleteMemory(editingMemory.id);
                    setShowMemoryForm(false);
                    setEditingMemory(null);
                  }}
                  style={{
                    padding: '12px 28px',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: 'white',
                    backgroundColor: '#dc2626',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Elimina
                </button>
              )}

              <button
                onClick={saveMemory}
                disabled={!newMemory.title || !newMemory.date}
                style={{
                  padding: '12px 28px',
                  fontSize: '15px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: (!newMemory.title || !newMemory.date) ? '#dadce0' : '#1a73e8',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (!newMemory.title || !newMemory.date) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(26, 115, 232, 0.3)'
                }}
              >
                {editingMemory ? 'Aggiorna ricordo' : 'Salva ricordo'}
              </button>
            </div>

          </div>
        </>
      )}

      {/* ❌ DEPRECATO v2.4: Modal dettagli umore rimosso (ora solo timeline) */}
      {/* ❌ DEPRECATO v3.0: Modal risultati AI separato rimosso (ora integrato nel modal principale) */}

    </div>
  );
}
